// src/pages/TopTrades.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";
import {
    QuickTradeEquityButton,
    QuickTradeOptionButton,
} from "../components/QuickTradeButtons";

const tabs = [
    { label: "Top Options Trades", endpoint: "/api/options-trades" },
    { label: "Top NYSE Picks", endpoint: "/api/nyse-picks" },
    { label: "Top NYSE Options", endpoint: "/api/nyse-options-trades" },
    { label: "Swing Trades", endpoint: "/api/swing-trades-live" },
];

const CLIENT_ITEMS_PER_PAGE = 10;
const SERVER_PAGED_TAB_INDEXES = new Set([2, 3]);

// helpers
const money2 = (v) =>
    v == null || v === ""
        ? "0.00"
        : Number.isFinite(Number(v))
            ? Number(v).toFixed(2)
            : String(v);

const pctChange = (a, b) => {
    const x = Number(a),
        y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y) || x === 0) return null;
    return ((y - x) / x) * 100;
};

const EstExitLine = ({ entry, exit }) => {
    const pct = pctChange(entry, exit);
    const good = pct !== null && pct >= 0;
    return (
        <div className="text-sm">
            Est Exit: ${money2(exit)}{" "}
            {pct !== null && (
                <span
                    className={`ml-1 px-2 py-0.5 rounded text-xs font-semibold ${good ? "bg-green-600" : "bg-red-600"
                        }`}
                >
                    {good ? "+" : ""}
                    {pct.toFixed(2)}%
                </span>
            )}
        </div>
    );
};

const TopTrades = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [trades, setTrades] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [clientPage, setClientPage] = useState(1);
    const [serverPage, setServerPage] = useState(1);
    const [serverPages, setServerPages] = useState(1);
    const [serverLimit, setServerLimit] = useState(50);
    const [sort, setSort] = useState("score");
    const [order, setOrder] = useState("desc");
    const navigate = useNavigate();

    const normalizeToArray = (data) =>
        Array.isArray(data) ? data : data?.results ?? [];

    useEffect(() => {
        const isServerPaged = SERVER_PAGED_TAB_INDEXES.has(activeTab);
        if (isServerPaged) setServerPage(1);
        else setClientPage(1);
    }, [activeTab]);

    useEffect(() => {
        let canceled = false;
        const fetchData = async () => {
            setLoading(true);
            setError("");
            try {
                const { endpoint } = tabs[activeTab];
                const isServerPaged = SERVER_PAGED_TAB_INDEXES.has(activeTab);
                const url = endpoint;
                const config = { timeout: 20000, params: {} };
                if (isServerPaged)
                    config.params = { page: serverPage, limit: serverLimit, sort, order };
                const { data } = await api.get(url, config);
                if (canceled) return;

                if (isServerPaged && Array.isArray(data.results)) {
                    setTrades(data.results);
                    setServerPages(data.pages || 1);
                } else {
                    setTrades(normalizeToArray(data));
                    setServerPages(1);
                }
            } catch (e) {
                if (!canceled) setError("Network error. Please try again.");
                console.error("API fetch error:", e);
            } finally {
                if (!canceled) setLoading(false);
            }
        };
        fetchData();
        return () => {
            canceled = true;
        };
    }, [activeTab, serverPage, serverLimit, sort, order]);

    // client paging
    const clientTotalPages =
        Math.ceil(trades.length / CLIENT_ITEMS_PER_PAGE) || 1;
    const clientSlice = trades.slice(
        (clientPage - 1) * CLIENT_ITEMS_PER_PAGE,
        clientPage * CLIENT_ITEMS_PER_PAGE
    );

    const isServerPaged = SERVER_PAGED_TAB_INDEXES.has(activeTab);
    const rows = isServerPaged ? trades : clientSlice;

    return (
        <div className="p-4 sm:p-5">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-400 drop-shadow">
                📊 Top Trades Today
            </h2>

            {/* Tabs */}
            <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-2 sm:space-y-0 mb-4 sm:mb-6">
                {tabs.map((tab, index) => (
                    <button
                        key={index}
                        onClick={() => setActiveTab(index)}
                        className={`px-4 py-2 rounded-xl font-semibold shadow transition-all ${activeTab === index
                                ? "bg-blue-600 ring-2 ring-blue-400"
                                : "bg-gray-700"
                            } hover:bg-blue-500`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Sort/Order controls for server-paged tabs */}
            {isServerPaged && (
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <label className="text-sm text-gray-300">
                        Show
                        <select
                            className="ml-2 bg-gray-800 rounded px-2 py-1"
                            value={serverLimit}
                            onChange={(e) => {
                                setServerPage(1);
                                setServerLimit(parseInt(e.target.value, 10));
                            }}
                        >
                            {[25, 50, 100, 200].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        / page
                    </label>

                    <label className="text-sm text-gray-300">
                        Sort by
                        <select
                            className="ml-2 bg-gray-800 rounded px-2 py-1"
                            value={sort}
                            onChange={(e) => {
                                setServerPage(1);
                                setSort(e.target.value);
                            }}
                        >
                            {/* For Top NYSE Options (tab index 2) */}
                            {activeTab === 2 && (
                                <>
                                    <option value="score">Score</option>
                                    <option value="ticker">Ticker</option>
                                    <option value="expiration">Expiration</option>
                                    <option value="strike">Strike</option>
                                    <option value="iv">IV</option>
                                    <option value="volume">Volume</option>
                                    <option value="openInterest">Open Interest</option>
                                    <option value="entryPrice">Entry</option>
                                    <option value="estimatedExit">Est Exit</option>
                                    <option value="confidence">Confidence</option>
                                    <option value="strategy">Strategy</option>
                                </>
                            )}

                            {/* For Swing Trades (tab index 3) */}
                            {activeTab === 3 && (
                                <>
                                    <option value="score">Score</option>
                                    <option value="ticker">Ticker</option>
                                    <option value="expiration">Expiration</option>
                                    <option value="entryPrice">Entry</option>
                                    <option value="estimatedExit">Est Exit</option>
                                    <option value="confidence">Confidence</option>
                                    <option value="strike">Strike</option>
                                    <option value="iv">IV</option>
                                    <option value="volume">Volume</option>
                                    <option value="openInterest">Open Interest</option>
                                </>
                            )}
                        </select>
                    </label>

                    <label className="text-sm text-gray-300">
                        Order
                        <select
                            className="ml-2 bg-gray-800 rounded px-2 py-1"
                            value={order}
                            onChange={(e) => {
                                setServerPage(1);
                                setOrder(e.target.value);
                            }}
                        >
                            <option value="desc">Desc</option>
                            <option value="asc">Asc</option>
                        </select>
                    </label>
                </div>
            )}

            {loading && <div className="text-gray-400">Loading…</div>}
            {error && <div className="text-red-400">{error}</div>}

            <div className="space-y-3">
                {rows.map((trade, index) => {
                    const rawStrategy = trade?.strategy;
                    const normalizedStrategy =
                        rawStrategy != null
                            ? String(rawStrategy).trim().toUpperCase()
                            : null;

                    const canTradeOption =
                        normalizedStrategy &&
                        trade?.strike != null &&
                        trade?.expiration;

                    const showStockOnlyNote =
                        activeTab === 3 && !canTradeOption; // Swing tab: some may be stock-only

                    const showOptionButton =
                        canTradeOption &&
                        (activeTab === 0 || activeTab === 2 || activeTab === 3);

                    return (
                        <div
                            key={index}
                            className="bg-gray-700 p-3 sm:p-4 rounded-lg shadow hover:bg-gray-600 cursor-pointer space-y-1"
                            onClick={() =>
                                trade.ticker && navigate(`/chart/${trade.ticker}`)
                            }
                        >
                            <div className="flex items-center text-lg sm:text-xl font-semibold">
                                <TickerWithName symbol={trade.ticker} />
                                {normalizedStrategy && (
                                    <span
                                        className={`ml-2 px-2 py-0.5 text-xs font-bold rounded ${normalizedStrategy === "CALL"
                                                ? "bg-green-600"
                                                : "bg-red-600"
                                            }`}
                                    >
                                        {normalizedStrategy}
                                    </span>
                                )}
                            </div>

                            {trade.score != null && (
                                <div className="text-sm text-gray-300">
                                    Score: {trade.score}
                                </div>
                            )}
                            {trade.strike != null && (
                                <div className="text-sm">Strike: {trade.strike}</div>
                            )}
                            {trade.expiration && (
                                <div className="text-sm">Expiration: {trade.expiration}</div>
                            )}

                            {trade.entryPrice != null && (
                                <div className="text-sm">
                                    Entry: ${money2(trade.entryPrice)}
                                </div>
                            )}
                            {trade.estimatedExit != null && (
                                <EstExitLine
                                    entry={trade.entryPrice}
                                    exit={trade.estimatedExit}
                                />
                            )}

                            {trade.confidence != null && (
                                <div className="text-sm text-blue-300">
                                    Confidence: {trade.confidence}%
                                </div>
                            )}
                            {trade.reason && (
                                <div className="text-sm text-gray-400">
                                    Reason: {trade.reason}
                                </div>
                            )}

                            {showStockOnlyNote && (
                                <div className="text-xs text-gray-400">
                                    Stock-only swing (no attached option).
                                </div>
                            )}

                            {/* 🔘 Quick trade buttons */}
                            <div className="mt-2 flex flex-wrap gap-2">
                                {showOptionButton && (
                                    <QuickTradeOptionButton
                                        ticker={trade.ticker}
                                        strategy={trade.strategy}
                                        strike={trade.strike}
                                        expiration={trade.expiration}
                                    />
                                )}

                                {trade.ticker && (
                                    <QuickTradeEquityButton ticker={trade.ticker} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* pagination */}
            {isServerPaged ? (
                <div className="flex justify-center items-center space-x-4 mt-6">
                    <button
                        disabled={serverPage === 1}
                        onClick={() => setServerPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-blue-500"
                    >
                        ⬅ Prev
                    </button>
                    <span className="text-gray-300">
                        Page {serverPage} of {serverPages}
                    </span>
                    <button
                        disabled={serverPage === serverPages}
                        onClick={() => setServerPage((p) => Math.min(serverPages, p + 1))}
                        className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-blue-500"
                    >
                        Next ➡
                    </button>
                </div>
            ) : (
                <div className="flex justify-center items-center space-x-4 mt-6">
                    <button
                        disabled={clientPage === 1}
                        onClick={() => setClientPage((p) => Math.max(1, p - 1))}
                        className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-blue-500"
                    >
                        ⬅ Prev
                    </button>
                    <span className="text-gray-300">
                        Page {clientPage} of {clientTotalPages}
                    </span>
                    <button
                        disabled={clientPage === clientTotalPages}
                        onClick={() =>
                            setClientPage((p) => Math.min(clientTotalPages, p + 1))
                        }
                        className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50 hover:bg-blue-500"
                    >
                        Next ➡
                    </button>
                </div>
            )}
        </div>
    );
};

export default TopTrades;
